import {
  LoadIcon,
  PlusIcon,
  UploadIcon,
  logoutIcon,
  adminIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu, CaptureUpdateAction } from "@excalidraw/excalidraw/index";
import React, { useRef } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";

import type { Theme } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { LanguageList } from "../app-language/LanguageList";
import { useSetAtom, useAtomValue, useAtom } from "../app-jotai";
import { customFileLibraryDialogAtom } from "../app-jotai";
import { CustomFileStorage } from "../data/customFileStorage";
import {
  authStateAtom,
  isAdminAtom,
  adminDialogStateAtom,
  userAtom,
} from "../auth/authAtoms";
import { logout as logoutApi } from "../auth/authApi";
import { clearAuthData } from "../auth/authStorage";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}> = React.memo((props) => {
  const setCustomLibraryDialog = useSetAtom(customFileLibraryDialogAtom);
  const setAppState = useExcalidrawSetAppState();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, setAuthState] = useAtom(authStateAtom);
  const isAdmin = useAtomValue(isAdminAtom);
  const user = useAtomValue(userAtom);
  const setAdminDialogState = useSetAtom(adminDialogStateAtom);

  const handleNewFile = async () => {
    // Clear current file tracking
    CustomFileStorage.clearCurrentFile();

    // Clear the canvas and create a new blank file
    if (props.excalidrawAPI) {
      props.excalidrawAPI.updateScene({
        elements: [],
        appState: {},
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local auth data
      clearAuthData();

      // Update auth state
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });

      // Reload the page to reset the app
      window.location.reload();
    }
  };

  const handleAdminClick = () => {
    setAdminDialogState({ isOpen: true });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      // Load the file data
      const data = await loadFromBlob(file, null, null);

      // Clear current file tracking to create a new file
      CustomFileStorage.clearCurrentFile();

      // Update the scene with the loaded data using the excalidraw API
      if (props.excalidrawAPI) {
        props.excalidrawAPI.updateScene({
          elements: data.elements,
          appState: data.appState,
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        // Add binary files if any
        if (data.files && Object.keys(data.files).length > 0) {
          props.excalidrawAPI.addFiles(Object.values(data.files));
        }
      }
    } catch (error) {
      console.error("Failed to load file:", error);
      setAppState({
        errorMessage: "Failed to load file. Please try again.",
      });
    }

    // Reset the input so the same file can be uploaded again
    if (event.target) {
      event.target.value = "";
    }
  };

  return (
    <MainMenu>
      <MainMenu.Item icon={PlusIcon} onClick={handleNewFile}>
        New File
      </MainMenu.Item>
      <MainMenu.Item icon={UploadIcon} onClick={handleUploadClick}>
        Upload
      </MainMenu.Item>
      <input
        ref={fileInputRef}
        type="file"
        accept=".excalidraw,.excalidrawlib"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.Item
        icon={LoadIcon}
        onClick={() => setCustomLibraryDialog({ isOpen: true })}
      >
        My Files
      </MainMenu.Item>
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      <MainMenu.Separator />
      {isAdmin && (
        <MainMenu.Item icon={adminIcon} onClick={handleAdminClick}>
          User Management
        </MainMenu.Item>
      )}
      <MainMenu.Item icon={logoutIcon} onClick={handleLogout}>
        Logout ({user?.username})
      </MainMenu.Item>
    </MainMenu>
  );
});
